class RingSizeSelector extends HTMLElement {
  #formatter = new Intl.NumberFormat(Shopify.locale, {
    style: "currency",
    currency: Shopify.currency.active
  });

  constructor() {
    super();
  }

  connectedCallback() {
    const inputs = this.querySelectorAll(".ring-size-selector__block--checkbox");
    const returnText = this.querySelector(".ring-size-selector--return-text");

    for (const input of inputs) {
      if (input.checked) {
        if (input.dataset.showReturnText == "") {
          returnText?.classList.add("show");
        } else {
          returnText?.classList.remove("show");
        }
      }

      input.addEventListener("change", () => {
        if (input.dataset.showReturnText == "") {
          returnText?.classList.add("show");
        } else {
          returnText?.classList.remove("show");
        }

        this.updateProductPrice();
      });
    }

    this.updateProductPrice();
    this.#bindFormEvents();
  }

  #bindFormEvents() {
    const formId = this.querySelector("input[form]")?.getAttribute("form");
    if (!formId) return;

    const form = document.getElementById(formId) || document.forms[formId];
    if (!form) return;

    const reapplyPrice = () => {
      setTimeout(() => this.updateProductPrice(), 0);
    };

    form.addEventListener("variant:change", reapplyPrice);
    form.addEventListener("product:rerender", reapplyPrice);
  }

  updateProductPrice() {
    const priceList = document.querySelector(".product-info--price price-list");
    if (!priceList) return;

    const checkedInput = this.querySelector(".ring-size-selector__block--checkbox:checked");
    const ringPrice = checkedInput
      ? parseInt(checkedInput.getAttribute("price") || "0", 10) || 0
      : 0;

    const salePrice = priceList.querySelector("sale-price");
    const compareAtPrice = priceList.querySelector("compare-at-price");

    if (salePrice?.dataset.price != null) {
      const basePrice = parseInt(salePrice.dataset.price, 10) || 0;
      this.#setPriceWithSurcharge(salePrice, basePrice, ringPrice);
    }

    if (compareAtPrice?.dataset.price != null) {
      const baseCompareAtPrice = parseInt(compareAtPrice.dataset.price, 10) || 0;
      this.#setPriceWithSurcharge(compareAtPrice, baseCompareAtPrice, 0);
    }
  }

  #setPriceWithSurcharge(element, baseCents, surchargeCents) {
    const srOnly = element.querySelector(".sr-only");
    element.replaceChildren();

    if (srOnly) {
      element.appendChild(srOnly);
    }

    element.append(` ${this.#formatMoney(baseCents)}`);

    if (surchargeCents > 0) {
      const surcharge = document.createElement("span");
      surcharge.className = "ring-size-price-surcharge";
      surcharge.textContent = ` (+ ${this.#formatMoney(surchargeCents)})`;
      element.appendChild(surcharge);
    }
  }

  #formatMoney(cents) {
    return this.#formatter.format(cents / 100).replace(/\s(?=\d)/, "");
  }
}

customElements.define("ring-size-selector", RingSizeSelector);

// Calendly
window.addEventListener("DOMContentLoaded", function() {
  const links = document.querySelectorAll('a[href*="#"]');

  for(const link of links) {
    const modalID = link.href.split('#')[1];
    const targetModal = document.querySelector(`.calendly--block[data-modal-id="${modalID}"]`);

    if(!targetModal) {
      continue
    }

    link.addEventListener("click", function(e) {
      e.preventDefault();
      targetModal.closest("rao-calendly").selectModal(modalID);
    });
  }
});

class RAOCalendly extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const buttons = this.querySelectorAll(".calendly--button");
    const blocks  = this.querySelectorAll(".calendly--block");

    for(const button of buttons) {
      button.addEventListener("click", this.selectBlock.bind(this, parseInt(button.dataset.index)));
    }
  }

  selectBlock(blockIndex) {
    const selectedButton = this.querySelector(".calendly--button.selected");
    const selectedBlock  = this.querySelector(".calendly--block.selected");

    if(selectedButton) {
      selectedButton.classList.remove("selected");
    }
    if(selectedBlock) {
      selectedBlock.classList.remove("selected");
    }

    const targetButton = this.querySelector(`.calendly--button[data-index="${blockIndex}"]`);
    const targetBlock  = this.querySelector(`.calendly--block[data-index="${blockIndex}"]`);

    if(!targetButton || !targetBlock) {
      console.error(`There is no block with this index [${blockIndex}]`);
      return
    }

    targetButton.classList.add("selected");
    targetBlock.classList.add("selected");

    this.style.setProperty("--transform-amount", `${-1 * blockIndex * 100}%`);
  }

  selectModal(modalID) {
    const targetBlock = this.querySelector(`.calendly--block[data-modal-id="${modalID}"]`);
    if(!targetBlock) {
      console.error(`There is no block with this modalID [${modalID}]`);
      return
    }

    this.selectBlock(parseInt(targetBlock.dataset.index));
    this.closest("rao-modal").show();
  }
}

customElements.define("rao-calendly", RAOCalendly);

// Price Table
class PriceTable extends HTMLElement {
  #formatter = new Intl.NumberFormat(Shopify.locale, { style: "currency", currency: Shopify.currency.active });

  constructor() {
    super();
  }

  async connectedCallback() {
    const resp = await(await fetch("https://vintage.abris.workers.dev/")).json();
    
    const updateDate = new Date(resp.updated_at);
    const goldPrice = resp.prices.gold.website_price_per_gram;
    const silverPrice = resp.prices.silver.website_price_per_gram;

    const goldPrices = this.querySelectorAll("[data-gold-amount]");
    this.loadGoldPrices(goldPrices, goldPrice);

    const silverPrices = this.querySelectorAll("[data-silver-amount]");
    this.loadSilverPrices(silverPrices, silverPrice);

    const lastRefreshEl = this.querySelector("[data-last-refresh-text]");
    this.loadDate(lastRefreshEl, updateDate);

    this.classList.add("loaded");
  }

  loadDate(el, date) {
    const text = el.dataset.lastRefreshText;
    const formattedText = this.#formatDateText(text, date);
    console.log(formattedText);

    el.innerText = formattedText;
  }

  loadGoldPrices(elements, unitPrice) {
    for(const el of elements) {
      const amount = parseInt(el.dataset.goldAmount);
      const price = this.getPrice(amount, unitPrice);

      el.innerText = this.#formatPrice(price);
    }
  }

  loadSilverPrices(elements, unitPrice) {
    for(const el of elements) {
      const amount = parseInt(el.dataset.silverAmount);
      const price = this.getPrice(amount, unitPrice);

      el.innerText = this.#formatPrice(price);
    }
  }

  getPrice(amount, unitPrice) {
    return (amount / 1000 * unitPrice).toFixed(2);
  }

  #formatDateText(text, date) {
    text = text.replace("%d", String(date.getDate()).padStart(2, "0"));
    text = text.replace("%m", String(date.getMonth() + 1).padStart(2, "0"));
    text = text.replace("%b", date.toLocaleString(Shopify.locale, {month: "short"}));
    text = text.replace("%B", date.toLocaleString(Shopify.locale, {month: "long"}));
    text = text.replace("%H", String(date.getHours()).padStart(2, "0"));
    text = text.replace("%M", String(date.getMinutes()).padStart(2, "0"));

    return text;
  }

  #formatPrice(value) {
    return this.#formatter.format(value).replace(/\s(?=\d)/, "");
  }
}

customElements.define("price-table", PriceTable);


/* Dynamic Breadcrumb Links */
function toHex(str) {
  return Array.from(str).map(c => c.charCodeAt(0).toString(16).padStart(2, "0")).join("");
}

/*window.addEventListener("DOMContentLoaded", function() {
  const productCards = document.querySelectorAll("product-card");

  if(productCards.length != 0) {
    const breadcrumbs = document.querySelector(".breadcrumb__list");

    if(breadcrumbs) {
      for(const productCard of productCards) {
        for(const breadcrumbLink of productCard.querySelectorAll(".product-card--breadcrumbs-link")) {
          let href = breadcrumbLink.getAttribute("href");

          if(href.includes("?")) {
            href += `&breadcrumbs=${toHex(breadcrumbs.outerHTML)}`;
          } else {
            href += `?breadcrumbs=${toHex(breadcrumbs.outerHTML)}`;
          }

          breadcrumbLink.setAttribute("href", href);
        }
      }
    }
  }
});*/

class RAOBreadcrumbs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handle = this.dataset.handle;
    const breadcrumbItems = this.getBreadcrumbsFromSession(handle);

    if(breadcrumbItems.length != 0) {
      this.buildBreadcrumbs(breadcrumbItems)
    } else {
      this.classList.add("show");
    }
  }

  getBreadcrumbsFromSession(handle) {
    const breadcrumbsMap  = localStorage.getItem("breadcrumbs") ? new Map(JSON.parse(localStorage.getItem("breadcrumbs"))) : null;
    if(breadcrumbsMap) {
      const breadcrumbsItem = breadcrumbsMap.get(handle);

      if(breadcrumbsItem && new Date(breadcrumbsItem.createdAt).setHours(24) < new Date()) {
        return "";
      }

      return breadcrumbsItem ? breadcrumbsItem.items : [];
    }

    return [];
  }

  decode(hexStr) {
    const bytes = Uint8Array.fromHex(hexStr);

    return new TextDecoder().decode(bytes);
  }

  buildBreadcrumbs(items) {
    const breadcrumbsHTML = "";
    const breadcrumbsParentTemp = this.querySelector(".breadcrumb__list");
    const breadcrumbsParent = breadcrumbsParentTemp.cloneNode(false);
    const breadcrumbsItemTemp   = this.querySelector(".breadcrumb__list-item");

    for(const item of items) {
      const breadcrumbsItem = breadcrumbsItemTemp.cloneNode(true);
      const breadcrumbsLink = breadcrumbsItem.querySelector("a");

      breadcrumbsLink.setAttribute("href", item.link);
      breadcrumbsLink.innerText = item.title;

      breadcrumbsParent.appendChild(breadcrumbsItem);
    }

    this.innerHTML = breadcrumbsParent.outerHTML;
    this.classList.add("show");
  }
}

customElements.define("rao-breadcrumbs", RAOBreadcrumbs);

class RAOModal extends HTMLElement {
  constructor() {
    super();
  }

  show() {
    this.classList.add("show");
    document.documentElement.classList.add("lock");
  }

  hide() {
    this.classList.remove("show");
    document.documentElement.classList.remove("lock");
  }
}

customElements.define("rao-modal", RAOModal);

class AppointmentModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const openButton = this.querySelector(".product-info__appointment_button");
    if (openButton) {
      openButton.addEventListener("click", this.open.bind(this));
    }

    const overlay = this.querySelector(".appointment--overlay");
    if (overlay) {
      overlay.addEventListener("click", this.close.bind(this));
    }

    const closeButton = this.querySelector(".appointment__modal--close");
    if (closeButton) {
      closeButton.addEventListener("click", this.close.bind(this));
    }

    this.content = this.querySelector(".appointment__modal--content");
    this.buttons = Array.from(this.querySelectorAll(".appointment__modal--header-button[data-appointment-panel]"));
    this.panelItems = Array.from(this.querySelectorAll(".appointment__modal--item[data-appointment-panel]"));
    this.staticItems = Array.from(this.querySelectorAll(".appointment__modal--item:not([data-appointment-panel])"));

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        this.showPanel(button.getAttribute("data-appointment-panel"));
      });
    });

    this.staticItems.forEach((item) => item.classList.add("is-active"));

    if (this.buttons.length > 0) {
      const activeButton = this.buttons.find((button) => button.classList.contains("button--active")) || this.buttons[0];
      this.showPanel(activeButton.getAttribute("data-appointment-panel"));
    } else {
      this.querySelectorAll(".appointment__modal--item").forEach((item) => item.classList.add("is-active"));
      if (this.content) {
        this.content.style.setProperty("--appointment-panel-index", "0");
      }
    }
  }

  showPanel(panelId) {
    this.buttons.forEach((button) => {
      button.classList.toggle("button--active", button.getAttribute("data-appointment-panel") === panelId);
    });

    this.panelItems.forEach((item) => {
      item.classList.toggle("is-active", item.getAttribute("data-appointment-panel") === panelId);
    });

    this.staticItems.forEach((item) => item.classList.add("is-active"));

    if (!this.content) return;

    const activeIndex = this.panelItems.findIndex((item) => item.getAttribute("data-appointment-panel") === panelId);
    this.content.style.setProperty("--appointment-panel-index", String(Math.max(activeIndex, 0)));
  }

  open() {
    this.classList.add("show-appointment-modal");
    document.querySelector(".product-info")?.classList.add("in-front");
    document.documentElement.classList.add("lock");
  }

  close() {
    this.classList.remove("show-appointment-modal");
    document.querySelector(".product-info")?.classList.remove("in-front");
    document.documentElement.classList.remove("lock");
  }
}

customElements.define("appointment-modal", AppointmentModal);

class RAODropdown extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.button  = this.querySelector(".dropdown--button");
    this.content = this.querySelector(".dropdown--content");
    this.inner   = this.querySelector(".dropdown--inner");
    const items  = this.querySelectorAll(".dropdown--item");
    this.error   = this.querySelector(".ring-size-selector--error");

    this.button.addEventListener("click", this.toggleButton.bind(this));

    for(const item of items) {
      item.addEventListener("click", this.itemSelect.bind(this, item));
    }
  }

  itemSelect(item) {
    const selectedItem = this.inner.querySelector(".selected");
    if(selectedItem) {
      selectedItem.classList.remove("selected");
    }

    this.button.querySelector(".dropdown--value").innerText = item.querySelector(".dropdown--item-label").innerText;
    this.button.classList.add("item-selected");

    this.error.classList.remove("show");
    
    this.toggleButton();

    item.classList.add("selected");
  }

  toggleButton() {
    this.content.style.setProperty("--inner-height", `${this.inner.getBoundingClientRect().height}px`);
    this.classList.toggle("open");
  }
}

customElements.define("rao-dropdown", RAODropdown);

// Countdown Timer
class CountdownTimer {
  constructor(element) {
    this.element = element;
    this.useOneTime = element.dataset.useOneTime === 'true';
    this.oneTimeDate = element.dataset.oneTimeDate;
    this.useLooping = element.dataset.useLooping === 'true';
    this.loopingDay = parseInt(element.dataset.loopingDay);
    this.loopingHour = parseInt(element.dataset.loopingHour);
    
    this.daysEl = element.querySelector('[data-days]');
    this.hoursEl = element.querySelector('[data-hours]');
    this.minutesEl = element.querySelector('[data-minutes]');
    this.secondsEl = element.querySelector('[data-seconds]');
    
    this.updateCountdown();
    this.intervalId = setInterval(() => this.updateCountdown(), 1000);
  }

  getTargetDate() {
    const now = new Date();

    if (this.useOneTime && this.oneTimeDate) {
      return this.parseOneTimeDate(this.oneTimeDate);
    }

    if (this.useLooping) {
      return this.getNextLoopingDate();
    }

    return null;
  }

  parseOneTimeDate(dateStr) {
    // Format: "MM-DD-HH" (e.g., "02-05-09")
    const [month, day, hour] = dateStr.split('-').map(Number);
    
    if (!month || !day || hour === undefined) {
      console.error('Invalid date format:', dateStr);
      return null;
    }

    const now = new Date();
    let year = now.getFullYear();
    
    // Create date for this year
    let targetDate = new Date(year, month - 1, day, hour, 0, 0, 0);
    
    // If the date has already passed this year, use next year
    if (targetDate < now) {
      year++;
      targetDate = new Date(year, month - 1, day, hour, 0, 0, 0);
    }
    
    return targetDate;
  }

  getNextLoopingDate() {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentHour = now.getHours();
    
    // Convert loopingDay (1-7, 1=Monday) to JavaScript day (0-6, 0=Sunday)
    let targetDayOfWeek = this.loopingDay % 7; // 1-6 stays same, 7 becomes 0 (Sunday)
    
    let daysUntilTarget = (targetDayOfWeek - currentDay + 7) % 7;
    
    // If today is the target day, check if the time has passed
    if (daysUntilTarget === 0) {
      if (currentHour >= this.loopingHour) {
        // Time has passed today, target is next week
        daysUntilTarget = 7;
      }
    }
    
    // Create target date
    const targetDate = new Date(now);
    targetDate.setDate(targetDate.getDate() + daysUntilTarget);
    targetDate.setHours(this.loopingHour, 0, 0, 0);
    
    return targetDate;
  }

  updateCountdown() {
    const targetDate = this.getTargetDate();
    
    if (!targetDate) {
      console.warn('No valid target date found');
      this.setDisplayValues(0, 0, 0, 0);
      return;
    }

    const now = new Date();
    const timeDiff = targetDate - now;
    
    if (timeDiff <= 0) {
      this.setDisplayValues(0, 0, 0, 0);
      
      // For looping countdown, refresh to get next target
      if (this.useLooping) {
        // The interval will pick up the new target automatically
      }
      return;
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
    
    this.setDisplayValues(days, hours, minutes, seconds);
  }

  setDisplayValues(days, hours, minutes, seconds) {
    this.daysEl.textContent = String(days).padStart(2, '0');
    this.hoursEl.textContent = String(hours).padStart(2, '0');
    this.minutesEl.textContent = String(minutes).padStart(2, '0');
    this.secondsEl.textContent = String(seconds).padStart(2, '0');
  }

  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}

// Initialize all countdown timers on page load
document.addEventListener('DOMContentLoaded', () => {
  const countdownElements = document.querySelectorAll('[data-countdown-timer]');
  countdownElements.forEach(el => {
    el.countdownTimer = new CountdownTimer(el);
  });
});

// Reinitialize countdown timers when new content is added (e.g., after pagination)
const observer = new MutationObserver(() => {
  const countdownElements = document.querySelectorAll('[data-countdown-timer]');
  countdownElements.forEach(el => {
    // Only initialize if not already initialized
    if (!el.countdownTimer) {
      console.log('Initializing new countdown element');
      el.countdownTimer = new CountdownTimer(el);
    }
  });
});

// Observe for new countdown elements
observer.observe(document.body, {
  childList: true,
  subtree: true
});

window.addEventListener("load", function() {
  const productPageElement = document.querySelector(".product-info__block-list");
  const specsButton        = document.querySelector(".specs--block-specs summary");

  if(productPageElement && specsButton) {
    const links = productPageElement.querySelectorAll("a");
    
    for(const link of links) {
      const href = link.getAttribute("href");

      if(href && href == "#specs") {
        link.addEventListener("click", function() {
          if(!specsButton.closest("details[open]")) {
            specsButton.click();
          }
        });
      }
    }
  }
});

class RecentlyViewedProductsRao extends HTMLElement {
  #isLoaded = false;

  connectedCallback() {
    this.#loadProducts();
  }

  async #loadProducts() {
    if (this.#isLoaded) return;
    this.#isLoaded = true;

    const section = this.closest(".shopify-section");
    if (!section) return;

    const items = this.#getStoredItems();
    const productsCount = parseInt(this.getAttribute("products-count") || "8", 10);
    const blocks = Array.from(this.querySelectorAll(".recently-viewed-rao__item"));
    const stackTemplate = this.querySelector(".recently-viewed-rao__stack-template");
    const carouselTemplate = this.querySelector(".recently-viewed-rao__carousel-template");
    const outerCarousel = this.querySelector("featured-collections-carousel");

    const blockProductIds = new Map();
    const idsToFetch = [];

    for (const block of blocks) {
      const handle = block.dataset.collectionHandle;
      if (!handle) {
        blockProductIds.set(block, []);
        continue;
      }

      const matchingIds = items
        .filter((item) => Array.isArray(item.collections) && item.collections.includes(handle))
        .map((item) => item.id)
        .slice(0, productsCount);

      blockProductIds.set(block, matchingIds);

      for (const id of matchingIds) {
        if (!idsToFetch.includes(id)) {
          idsToFetch.push(id);
        }
      }
    }

    if (idsToFetch.length === 0) {
      section.remove();
      return;
    }

    const query = idsToFetch.map((id) => `id:${id}`).join(" OR ");
    const sectionId = section.id.replace("shopify-section-", "");
    const url = `${Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=${sectionId}`;

    try {
      const response = await fetch(url, { priority: "low" });
      const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html");
      const cardsRoot = tempDiv.querySelector(".recently-viewed-rao__cards");

      if (!cardsRoot) {
        section.remove();
        return;
      }

      const cardsById = new Map();
      for (const cardWrapper of cardsRoot.querySelectorAll("[data-product-id]")) {
        cardsById.set(parseInt(cardWrapper.dataset.productId, 10), cardWrapper);
      }

      const emptyIndexes = [];

      for (let index = 0; index < blocks.length; index++) {
        const block = blocks[index];
        const matchingIds = blockProductIds.get(block) || [];
        const listHost = block.querySelector(".recently-viewed-rao__list-host");
        const handle = block.dataset.collectionHandle;
        const singleTitle = this.querySelector(
          `.recently-viewed-rao__single-title[data-collection-handle="${CSS.escape(handle)}"]`
        );

        const cards = [];
        for (const id of matchingIds) {
          const card = cardsById.get(id);
          if (card) {
            cards.push(document.importNode(card.firstElementChild || card, true));
          }
        }

        if (!cards.length || !listHost) {
          emptyIndexes.push(index);
          block.classList.remove("is-selected");
          if (singleTitle) singleTitle.hidden = true;
          continue;
        }

        const productList = this.#buildProductList(listHost, cards, stackTemplate, carouselTemplate);
        if (!productList) {
          emptyIndexes.push(index);
          block.classList.remove("is-selected");
          if (singleTitle) singleTitle.hidden = true;
          continue;
        }

        listHost.replaceWith(productList);
      }

      if (emptyIndexes.length === blocks.length) {
        section.remove();
        return;
      }

      if (outerCarousel && typeof outerCarousel.filter === "function") {
        outerCarousel.filter(emptyIndexes);
      } else {
        emptyIndexes.forEach((index) => {
          blocks[index].hidden = true;
          const handle = blocks[index].dataset.collectionHandle;
          const navButton = this.querySelector(
            `.recently-viewed-rao__navigation [data-collection-handle="${CSS.escape(handle)}"]`
          );
          if (navButton) navButton.hidden = true;
        });
      }

      this.#bindCarouselRefresh();
      await this.#activateCategory(0);
      this.setAttribute("data-loaded", "");
    } catch (error) {
      section.remove();
    }
  }

  #buildProductList(listHost, cards, stackTemplate, carouselTemplate) {
    const isStack = listHost.hasAttribute("data-stack-products");

    if (isStack) {
      if (!stackTemplate) return null;
      const productList = stackTemplate.content.firstElementChild.cloneNode(true);
      productList.replaceChildren(...cards);
      return productList;
    }

    if (!carouselTemplate) return null;

    const carouselId = listHost.dataset.carouselId;
    const productList = carouselTemplate.content.firstElementChild.cloneNode(true);

    productList.querySelectorAll("[aria-controls]").forEach((el) => {
      el.setAttribute("aria-controls", carouselId);
    });

    const scrollCarousel = productList.querySelector("scroll-carousel");
    if (!scrollCarousel) return null;

    scrollCarousel.id = carouselId;
    scrollCarousel.replaceChildren(...cards);

    return productList;
  }

  /**
   * Force the first visible category active after async inject.
   * EffectCarousel.select() no-ops when index === selectedIndex, so nav + is-selected
   * would otherwise stay unset and product cards could remain opacity:0 from motion styles.
   */
  async #activateCategory(index) {
    const outerCarousel = this.querySelector("featured-collections-carousel");
    const cells = outerCarousel?.cells ? Array.from(outerCarousel.cells) : [];
    const cell = cells[index];
    if (!cell) return;

    // Clear leave/enter animation leftovers on every category panel.
    if (outerCarousel?.allCells) {
      outerCarousel.allCells.forEach((slide) => {
        slide.style.removeProperty("opacity");
        slide.style.removeProperty("transform");
        slide.style.removeProperty("visibility");
        slide.classList.toggle("is-selected", slide === cell);
      });
    } else {
      cell.classList.add("is-selected");
    }

    // Cards injected after inView already fired can stay at opacity:0 via reveal-on-scroll CSS.
    cell.querySelectorAll("[reveal-on-scroll]").forEach((element) => {
      element.removeAttribute("reveal-on-scroll");
      element.style.removeProperty("opacity");
      element.style.removeProperty("transform");
    });

    const navButtons = Array.from(
      this.querySelectorAll(".recently-viewed-rao__navigation button")
    ).filter((button) => !button.hasAttribute("hidden"));

    navButtons.forEach((button, buttonIndex) => {
      button.setAttribute("aria-current", buttonIndex === index ? "true" : "false");
    });

    // Sync EffectCarousel internal index when it is not already on this cell.
    if (outerCarousel && typeof outerCarousel.select === "function" && outerCarousel.selectedIndex !== index) {
      await outerCarousel.select(index, { instant: true });
    }

    this.#refreshProductCarousel(cell);
  }

  #refreshProductCarousel(cell) {
    const scrollCarousel = cell?.querySelector("scroll-carousel");
    if (!scrollCarousel || typeof scrollCarousel.select !== "function") return;

    requestAnimationFrame(() => {
      try {
        scrollCarousel.select(0, { instant: true });
      } catch (error) {
        // Ignore if carousel has no measurable cells yet.
      }
    });
  }

  #bindCarouselRefresh() {
    const outerCarousel = this.querySelector("featured-collections-carousel");
    if (!outerCarousel || outerCarousel.dataset.raoRefreshBound) return;

    outerCarousel.dataset.raoRefreshBound = "true";
    outerCarousel.addEventListener("carousel:settle", (event) => {
      const cell = event.detail?.cell;
      if (!cell) return;

      cell.querySelectorAll("[reveal-on-scroll]").forEach((element) => {
        element.removeAttribute("reveal-on-scroll");
        element.style.removeProperty("opacity");
        element.style.removeProperty("transform");
      });

      this.#refreshProductCarousel(cell);
    });
  }

  #getStoredItems() {
    try {
      const items = JSON.parse(localStorage.getItem("theme:recently-viewed-products-rao") || "[]");
      if (!Array.isArray(items)) return [];

      const excludeId = this.hasAttribute("exclude-id")
        ? parseInt(this.getAttribute("exclude-id"), 10)
        : null;

      return items.filter((item) => {
        if (!item || typeof item.id !== "number") return false;
        if (excludeId != null && item.id === excludeId) return false;
        return true;
      });
    } catch (error) {
      return [];
    }
  }
}

customElements.define("recently-viewed-products-rao", RecentlyViewedProductsRao); 